// 发送消息功能
import { atom } from 'jotai';
import i18n from '../../../lib/i18n';
import { fetchApi } from '../../../lib/apiClient';
import { 
  SendMessageResponse, 
  Message, 
  INITIAL_PLACEHOLDER_ID 
} from './types';
import { 
  selectedConversationIdAtom, 
  messagesAtom, 
  isSendingAtom, 
  pendingMessagesStorage,
  loadableStreamingChatEnabledAtom
} from './baseAtoms';
import { selectedCharacterIdAtom } from '../characterAtoms';
import { fetchConversationsAtom } from './conversationAtoms';
import {  sendChatNotification } from './messageAtoms';
import { handleStreamingChatRequest } from './streamingChatAtoms';
import { v4 as uuidv4 } from 'uuid';

/**
 * 发送消息
 */
export const sendMessageAtom = atom(
  null,
  async (get, set, params: { text: string; mediaId?: string }) => {
    const { text, mediaId } = params;
    let currentConversationId = get(selectedConversationIdAtom);
    const isTempNewConversation = currentConversationId === 'new';
    // 获取当前选择的角色卡ID
    const characterId = get(selectedCharacterIdAtom);

    if (!text.trim() && !mediaId) return;

    if (get(isSendingAtom)) return;

    // 创建用户消息和初始AI占位符消息
    const userTimestamp = new Date().toISOString();
    const userMessage: Message = {
      id: `temp-user-${Date.now()}`,
      role: 'user',
      content: [
        { type: 'text', text: text.trim() }
      ],
      timestamp: userTimestamp,
      mediaIds: mediaId ? [mediaId] : undefined,
      metadata: { stableKey: uuidv4() } // Also give user message stable key
    };

    // Create the placeholder for streaming case (but don't add it yet)
    const initialPlaceholderMessage: Message = {
      id: `final-ai-${Date.now()}`, 
      role: 'assistant',
      content: [{ type: 'text', text: i18n.t('v3/chat:messages.thinking', '正在思考中...') }],
      timestamp: new Date(new Date(userTimestamp).getTime() + 1).toISOString(),
      metadata: {
        status: 'loading',
        streaming: true,
        stableKey: uuidv4(),
        displayType: 'placeholder'
      }
    };

    // Optimistic UI: Add ONLY the user message immediately
    const activeConvId = get(selectedConversationIdAtom);
    if (activeConvId === currentConversationId) {
        console.log(`[sendMessageAtom] Optimistically adding user message ${userMessage.id} (stableKey: ${userMessage.metadata?.stableKey})`);
        set(messagesAtom, (prevMessages = []) => [...prevMessages, userMessage]);
    } else {
        console.warn("[sendMessageAtom] Conversation changed before adding user message.");
        return; 
    }

    set(isSendingAtom, true);
    const pendingId = `pending-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    set(pendingMessagesStorage, prev => ({ ...prev, [pendingId]: { conversationId: currentConversationId || '', messageText: text, timestamp: userTimestamp } }));

    try {
        const requestBody = {
          conversationId: isTempNewConversation ? undefined : currentConversationId,
          text: text.trim(),
          mediaId: mediaId,
          characterId: characterId
        };

        // --- Correctly access loadable atom data --- 
        const streamingEnabledLoadable = get(loadableStreamingChatEnabledAtom);
        const streamingEnabled = streamingEnabledLoadable.state === 'hasData' 
                                  ? streamingEnabledLoadable.data 
                                  : false; // Default to false if loading or error
        if (streamingEnabledLoadable.state === 'loading') {
            console.warn('[sendMessageAtom] Streaming chat setting still loading, defaulting to non-streaming.');
        } else if (streamingEnabledLoadable.state === 'hasError') {
            console.error('[sendMessageAtom] Error loading streaming chat setting, defaulting to non-streaming.');
        }
        // -------------------------------------------

        if (streamingEnabled) {
            const responseData = await handleStreamingChatRequest(
              get,
              set,
              requestBody,
              userMessage, 
              initialPlaceholderMessage, // Pass the placeholder object
              isTempNewConversation,
              currentConversationId
            );
            return responseData;
        } else {
           // Non-streaming case: Add its own placeholder first
           const nonStreamPlaceholder: Message = { 
               ...initialPlaceholderMessage, 
               id: INITIAL_PLACEHOLDER_ID, // Use constant ID for non-streaming 
               metadata: { 
                 ...(initialPlaceholderMessage.metadata || {}), 
                 streaming: false, // Mark as non-streaming 
                 displayType: undefined // Not needed for non-streaming
               }
           }; 
           set(messagesAtom, (prevMessages = []) => [...prevMessages, nonStreamPlaceholder]);

           const responseData = await fetchApi<SendMessageResponse>('/chat/send', {
             method: 'POST',
             body: requestBody,
           });

           const actualConversationId = responseData.conversationId;
           const aiMessage = responseData.aiMessage;
           const currentSelectedIdAfterAPI = get(selectedConversationIdAtom);

           if (isTempNewConversation && actualConversationId && currentSelectedIdAfterAPI === 'new') {
               set(selectedConversationIdAtom, actualConversationId);
           }

           if (aiMessage && typeof aiMessage === 'object' && aiMessage.content !== undefined) {
             const shouldUpdateMessages =
               (isTempNewConversation && currentSelectedIdAfterAPI === actualConversationId) ||
               (!isTempNewConversation && currentSelectedIdAfterAPI === currentConversationId);

             if (shouldUpdateMessages) {
                 aiMessage.metadata = { ...(aiMessage.metadata || {}), stableKey: nonStreamPlaceholder.metadata?.stableKey || uuidv4() };
                 set(messagesAtom, (prevMessages = []) => {
                   const filteredMessages = prevMessages.filter(msg => msg.id !== INITIAL_PLACEHOLDER_ID);
                   if (filteredMessages.find(msg => msg.id === aiMessage.id)) {
                     return filteredMessages;
                   }
                   return [...filteredMessages, aiMessage];
                 });
             } else {
               console.warn("[sendMessageAtom - NonStream] Conversation changed, new messages not added.");
             }
             await sendChatNotification(actualConversationId, aiMessage);
           } else {
              console.error('[sendMessageAtom - NonStream] Invalid AI response:', responseData);
              const shouldCleanup = (isTempNewConversation && currentSelectedIdAfterAPI === actualConversationId) || (!isTempNewConversation && currentSelectedIdAfterAPI === currentConversationId);
              if (shouldCleanup) {
                set(messagesAtom, prev => prev.filter(msg => msg.id !== INITIAL_PLACEHOLDER_ID));
              }
           }
           set(fetchConversationsAtom); 
           return responseData;
        }
    } catch (error) {
        console.error('[sendMessageAtom] Failed to send message:', error);
        const currentSelectedIdAfterError = get(selectedConversationIdAtom);
        const shouldCleanupOnError = (isTempNewConversation && currentSelectedIdAfterError === 'new') || (!isTempNewConversation && currentSelectedIdAfterError === currentConversationId);

        // Clean placeholder only if non-streaming and conv didn't change
        if (shouldCleanupOnError) {
           set(messagesAtom, prev => prev.filter(msg => msg.id === INITIAL_PLACEHOLDER_ID)); 
        }
        throw error;
    } finally {
      set(isSendingAtom, false);
      set(pendingMessagesStorage, prev => {
         const newPending = { ...prev };
         delete newPending[pendingId];
         return newPending;
      });
    }
  }
);
